import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AssistantConversationDTO, AssistantPendingDTO } from '@contracts/assistant';
import { ORDER_BOUNDS, type OrderCardValue, type OrderUseItem } from '@contracts/assistant-orders';
import type { Id } from '@contracts/common';
import { ApiClientError, offlineError } from '../../api/client';
import { TEXT } from '../../components/typography';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { cn } from '../../lib/utils';
import { avatarTone, serverInitial } from '../assistant-admin/server-tone';
import { useAssistantOptional } from './AssistantProvider';
import { conversationKey, editAction, useOwnAppearance } from './api';
import { LEVEL_LABEL } from './autonomy/labels';
import { Hana } from './Hana';
import { ORDER_CARD } from './missions/labels';
import { PendingWhy } from './PendingWhy';
import { DEFAULT_PERSONA_NAME } from '@contracts/persona';

/**
 * The `order` confirm card (S7 §7.1, P7, P21, D201, D222; mockup §3 left :330-340): a standing order Hana proposes or
 * a change to one (`order.update`, titled "Sửa lệnh thường trực"), as the
 * owner will live with it — its name, their instruction verbatim (plain text, line breaks kept, never markdown), when it
 * runs, what it expects to use (P8, informational), and its autonomy level with the line that sums it up. Every word
 * but the labels comes from the server's card value. `Sửa` rewords the name and the instruction in place and sends them
 * through the edit endpoint; when it runs and how freely are changed by asking Hana, never on the card.
 */

const MUTED = 'text-[hsl(var(--h-muted-foreground))]';
const KEY = `text-[11px] font-bold uppercase tracking-[0.04em] ${MUTED}`;
const VALUE = 'leading-normal text-[hsl(var(--h-card-foreground))] [overflow-wrap:anywhere]';
const FAILED = 'Đã có lỗi xảy ra, vui lòng thử lại';
/** Sửa's instruction field grows with its text up to this (Tailwind `max-h-80`), then scrolls. */
const INSTRUCTION_MAX_PX = 320;

/** The card value of a pending `order.create` (D201) or `order.update` (D222), or null when the input carries none. */
export function orderOf(pending: AssistantPendingDTO): OrderCardValue | null {
  const value = pending.input.order;
  return (pending.capability === 'order.create' || pending.capability === 'order.update') && typeof value === 'object' && value !== null
    && typeof (value as OrderCardValue).instruction === 'string' && Array.isArray((value as OrderCardValue).uses)
    ? value as OrderCardValue : null;
}

/** One labelled row of the card (mockup `.kv`): the small uppercase key over its value; `extra` trails the key. */
function Row({ label, extra, children }: { label: string; extra?: ReactNode; children: (labelId: string) => ReactNode }) {
  const id = useId();
  return (
    <div role="group" aria-labelledby={id} className="flex min-w-0 flex-col gap-0.5">
      <div className={KEY}>
        <span id={id}>{label}</span>
        {extra !== undefined && <span className="font-normal normal-case tracking-normal"> · {extra}</span>}
      </div>
      {children(id)}
    </div>
  );
}

/** "Dùng" (P8): a server as its letter square and name, a project as a chip; "·" between them. */
function Uses({ uses }: { uses: readonly OrderUseItem[] }) {
  if (uses.length === 0) return <p className={cn(VALUE, MUTED)}>{ORDER_CARD.noUses}</p>;
  return (
    <p className={cn(VALUE, 'flex flex-wrap items-center gap-x-1.5 gap-y-1')}>
      {uses.map((u, i) => (
        <span key={`${u.kind}:${u.id}`} className="inline-flex min-w-0 max-w-full items-center gap-1.5">
          {i > 0 && <span aria-hidden="true" className={MUTED}>·</span>}
          {u.kind === 'server' ? (
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <span data-srv="" aria-hidden="true" className={cn(
                'grid size-4 flex-none place-items-center rounded-[4px] text-[9px] font-extrabold leading-none text-white',
                avatarTone(u.id))}>
                {serverInitial(u.name)}
              </span>
              <span className="min-w-0 truncate">{u.name}</span>
            </span>
          ) : (
            <span title={u.name}
              className="inline-block min-w-0 max-w-full truncate rounded-[10px] bg-[hsl(var(--h-primary)/0.08)] px-2 py-px text-xs font-semibold text-[hsl(var(--h-primary))]">
              {ORDER_CARD.project(u.name)}
            </span>
          )}
        </span>
      ))}
    </p>
  );
}

/** The level line, its level's name in bold when it leads the line (the server's words, D205). */
function LevelLine({ value }: { value: OrderCardValue }) {
  const name = LEVEL_LABEL[value.level];
  if (!value.levelLine.startsWith(name)) return <p className={VALUE}>{value.levelLine}</p>;
  return <p className={VALUE}><b className="font-bold">{name}</b>{value.levelLine.slice(name.length)}</p>;
}

export function OrderCard({ pending, value, disabled, onAct, readOnly, className, onEdited, aside }: {
  pending: AssistantPendingDTO; value: OrderCardValue; disabled: boolean;
  onAct: (actionId: Id, kind: 'confirm' | 'reject') => void;
  /** A card shown where it is not the owner's to reword (a page that is not the chat): read and approve only. */
  readOnly: boolean; className?: string; onEdited?: (next: AssistantPendingDTO) => void; aside?: ReactNode;
}) {
  const appearance = useOwnAppearance();
  const assistant = useAssistantOptional();
  const queryClient = useQueryClient();
  const persona = assistant?.prefs.personaName ?? DEFAULT_PERSONA_NAME;
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(value.name);
  const [instruction, setInstruction] = useState(value.instruction);
  const instructionRef = useRef<HTMLTextAreaElement>(null);
  // Sửa shows the whole instruction (G10): the field is as tall as its text, up to the cap, never a fixed 4 rows.
  useLayoutEffect(() => {
    const el = instructionRef.current;
    if (el === null) return;
    el.style.height = 'auto';
    // jsdom has no layout (scrollHeight 0): the field keeps its rows there.
    if (el.scrollHeight <= 0) return;
    el.style.height = `${Math.min(el.scrollHeight + el.offsetHeight - el.clientHeight, INSTRUCTION_MAX_PX)}px`;
  }, [editing, instruction]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // One request id per open form: a double press on Lưu is one edit.
  const requestId = useRef(crypto.randomUUID());
  const editButton = useRef<HTMLButtonElement>(null);
  const backToEdit = useRef(false);
  const base = useId();
  const busy = disabled || saving;
  // D222: a change to an order is the same card, the order as it will be, titled as a change.
  const heading = pending.capability === 'order.update' ? ORDER_CARD.updateHeading : ORDER_CARD.heading;

  useEffect(() => {
    if (!editing && backToEdit.current) { backToEdit.current = false; editButton.current?.focus(); }
  }, [editing]);

  const open = () => {
    if (busy) return;
    setName(value.name);
    setInstruction(value.instruction);
    setErrors({});
    setFailure(null);
    requestId.current = crypto.randomUUID();
    setEditing(true);
  };
  const close = () => { backToEdit.current = true; setEditing(false); };

  const save = async () => {
    if (busy || name.trim() === '' || instruction.trim() === '') return;
    if (name === value.name && instruction === value.instruction) { close(); return; }
    setSaving(true);
    setErrors({});
    setFailure(null);
    try {
      // D201: the stored input echoed whole with the new name and instruction (the instruction as typed, verbatim);
      // the server keeps every other key as stored (`narrowedOrder`) and builds the card again.
      const next = await editAction(pending.actionId, { input: { ...pending.input, name, instruction } }, requestId.current);
      const cid = assistant?.conversationId ?? null;
      if (cid !== null) {
        queryClient.setQueryData<AssistantConversationDTO>(conversationKey(cid), (c) => (c === undefined ? c
          : { ...c, pending: c.pending.map((p) => (p.actionId === pending.actionId ? next : p)) }));
      }
      onEdited?.(next);
      close();
    } catch (e) {
      const err = e instanceof ApiClientError ? e : offlineError();
      if (err.code === 'VALIDATION' && err.fields !== undefined
        && (err.fields.name !== undefined || err.fields.instruction !== undefined)) setErrors(err.fields);
      else setFailure(err.message || FAILED);
      requestId.current = crypto.randomUUID();
    } finally {
      setSaving(false);
    }
  };

  const errorOf = (field: 'name' | 'instruction') => {
    const error = errors[field];
    const id = `${base}-${field}-error`;
    return { error, id: error === undefined ? undefined : id,
      line: error === undefined ? null : <p id={id} className={TEXT.fieldError}>{error}</p> };
  };
  const nameError = errorOf('name');
  const instructionError = errorOf('instruction');

  return (
    <section role="group" aria-label={heading} data-action={pending.actionId} data-order-card=""
      className={cn('mr-auto w-full min-w-0 max-w-[92%] overflow-hidden rounded-[10px] border border-[hsl(var(--h-border))] bg-[hsl(var(--h-card))] text-sm', className)}>
      <div className="flex items-center gap-2 px-3 pb-1 pt-2.5">
        <Hana appearance={appearance} emotion="ask" size={22} still={assistant?.still ?? true} className="shrink-0" />
        <b className="min-w-0 flex-1 font-bold text-[hsl(var(--h-card-foreground))]">{heading}</b>
      </div>
      {/* S6 §7.5: a conversation that read outside content says so; S7 §9.5: why autonomy asked. */}
      <PendingWhy pending={pending} inset />

      <form className="contents" onSubmit={(e) => { e.preventDefault(); void save(); }}
        onKeyDown={(e) => { if (editing && e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); } }}>
        <div className="flex flex-col gap-2.5 px-3 py-2">
          <Row label={ORDER_CARD.name}>
            {(labelId) => (editing ? (
              <>
                <Input aria-labelledby={labelId} value={name} autoFocus maxLength={ORDER_BOUNDS.name.max} disabled={saving}
                  aria-invalid={nameError.error === undefined ? undefined : true} aria-describedby={nameError.id}
                  onChange={(e) => setName(e.target.value)} />
                {nameError.line}
              </>
            ) : <p className={cn(VALUE, 'font-semibold')}>{value.name}</p>)}
          </Row>
          <Row label={ORDER_CARD.instruction} extra={ORDER_CARD.verbatim}>
            {(labelId) => (editing ? (
              <>
                <Textarea ref={instructionRef} aria-labelledby={labelId} value={instruction} rows={3}
                  maxLength={ORDER_BOUNDS.instruction.max} disabled={saving} className="max-h-80 resize-none overflow-y-auto"
                  aria-invalid={instructionError.error === undefined ? undefined : true}
                  aria-describedby={instructionError.id} onChange={(e) => setInstruction(e.target.value)} />
                {instructionError.line}
              </>
            ) : (
              // P7: verbatim — plain text, its line breaks and indents kept, never markdown.
              <p data-instruction="" className={cn(VALUE, 'whitespace-pre-wrap')}>{value.instruction}</p>
            ))}
          </Row>
          <Row label={ORDER_CARD.when}>{() => <p className={VALUE}>{value.when}</p>}</Row>
          <Row label={ORDER_CARD.uses}>{() => <Uses uses={value.uses} />}</Row>
          <Row label={ORDER_CARD.level}>{() => <LevelLine value={value} />}</Row>
        </div>

        <div className="border-t border-[hsl(var(--h-border))] px-3 py-2.5">
          {editing ? (
            <>
              {failure !== null && <p role="alert" className={cn(TEXT.fieldError, 'mb-2')}>{failure}</p>}
              <div className="flex flex-wrap items-center gap-2">
                <Button type="submit" size="sm" disabled={busy || name.trim() === '' || instruction.trim() === ''}>
                  {ORDER_CARD.save}
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={close}>{ORDER_CARD.cancel}</Button>
              </div>
              <p className={cn('mt-1.5 text-xs leading-normal', MUTED)}>{ORDER_CARD.editNote(persona)}</p>
            </>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" size="sm" disabled={busy} onClick={() => onAct(pending.actionId, 'confirm')}>
                {ORDER_CARD.confirm}
              </Button>
              {!readOnly && (
                <Button ref={editButton} type="button" size="sm" variant="outline" disabled={busy} onClick={open}>
                  {ORDER_CARD.edit}
                </Button>
              )}
              <Button type="button" size="sm" variant="outline" disabled={busy}
                onClick={() => onAct(pending.actionId, 'reject')}>{ORDER_CARD.reject}</Button>
              {aside !== undefined && <span className={cn('ml-auto text-xs', MUTED)}>{aside}</span>}
            </div>
          )}
        </div>
      </form>
    </section>
  );
}
